// serviceFeeViewUtils.js (ESB-639)
//
// Display helpers for the So-Strom service fees. The list shows one joined
// value string per fee entry (fixed, per kWh, per community, per metering
// point, the scale steps) with the minimum amount written into the last
// element, as agreed in the meeting on 2026-09-16.
// Kept self contained so the list and these helpers can be lifted into
// common components once reloaded shows the fee table read only.

// mirror of data-models constants/serviceFeeEnums.js
export const BILLING_VARIANTS = Object.freeze({
  CC_SEPERATE: 'CC_SEPERATE',
  CP_VOLUME: 'CP_VOLUME',
  CC_VOLUME: 'CC_VOLUME',
});

// minor units (cents) to a fixed decimal EUR string, deterministic like the
// accountability formatter (comma decimals, dot thousands)
export function formatMinorEur(minor) {
  if (minor === undefined || minor === null) return '';
  const negative = minor < 0;
  const abs = Math.abs(Math.round(minor));
  const euros = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, '0');
  let intStr = String(euros);
  let grouped = '';
  while (intStr.length > 3) {
    grouped = '.' + intStr.slice(-3) + grouped;
    intStr = intStr.slice(0, -3);
  }
  return (negative ? '-' : '') + intStr + grouped + ',' + cents;
}

// tariff/fraction pair to an EUR per kWh display string (up to 6 decimals, trimmed)
export function formatPerKwhEur(pricePerkWh, fraction = 6) {
  if (pricePerkWh === undefined || pricePerkWh === null) return '';
  const value = pricePerkWh * Math.pow(10, -fraction);
  let str = value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.0');
  return str.replace('.', ',');
}

function hasValue(v) {
  return v !== undefined && v !== null;
}

// one joined display string for the fee entry, the common cases from the
// meeting: "0,061 €/kWh", "2.500,00 € + 2,50 €/ZP mtl.", scale steps like
// "1-10: 2,50 €/ZP mtl." with the minimum amount in the last element
export function serviceFeePriceDisplay(fee) {
  if (!fee) return '';
  const parts = [];

  if (hasValue(fee.priceFixedMinor))
    parts.push(formatMinorEur(fee.priceFixedMinor) + ' € einmalig');

  if (hasValue(fee.pricePerkWh))
    parts.push(formatPerKwhEur(fee.pricePerkWh, fee.pricePerkWhFraction) + ' €/kWh');

  if (hasValue(fee.pricePerCommunityMonthlyMinor))
    parts.push(formatMinorEur(fee.pricePerCommunityMonthlyMinor) + ' €/Gemeinschaft mtl.');

  if (hasValue(fee.pricePerMeteringPointMonthlyMinor))
    parts.push(formatMinorEur(fee.pricePerMeteringPointMonthlyMinor) + ' €/ZP mtl.');

  const scale = fee.meteringPointPriceScaleMonthlyMinor || [];
  for (const step of scale) {
    const range = hasValue(step.endAmount)
      ? step.startAmount + '-' + step.endAmount
      : 'ab ' + step.startAmount;
    parts.push(range + ': ' + formatMinorEur(step.pricePerUnitMonthlyMinor) + ' €/ZP mtl.');
  }

  if (hasValue(fee.minimumAmountMonthlyMinor))
    parts.push('min. ' + formatMinorEur(fee.minimumAmountMonthlyMinor) + ' € mtl.');

  return parts.join(' + ');
}

// EUR decimal input (string or number, comma or dot decimals) to integer minor
// units; with a comma present dots count as thousand separators
export function eurInputToMinor(input) {
  if (input === undefined || input === null || input === '') return undefined;
  let normalized = String(input).trim();
  if (normalized.includes(','))
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  else if (!/^-?\d+\.\d{1,2}$/.test(normalized))
    normalized = normalized.replace(/\./g, '');
  const value = Number(normalized);
  if (Number.isNaN(value)) return undefined;
  return Math.round(value * 100);
}

// integer minor units to an EUR input value (comma decimals, matching what
// eurInputToMinor reads back so edit round trips keep the amount)
export function minorToEurInput(minor) {
  if (minor === undefined || minor === null) return '';
  return (minor / 100).toFixed(2).replace('.', ',');
}

// EUR per kWh decimal input to the tariff/fraction pair with fraction 6
export function perKwhInputToTariff(input) {
  if (input === undefined || input === null || input === '') return undefined;
  const value = Number(String(input).replace(',', '.'));
  if (Number.isNaN(value)) return undefined;
  return Math.round(value * 1e6);
}
